import useTracksStore, {
  TrackType,
} from '../../../../shared/store/tracks.store.tsx'
import { addDraggingClip } from './add-dragging-clip.ts'
import { generateSoundDesign } from '../../../sound-design/domain/use-cases/generate-sound-design.ts'
import useMediaStore from '../../../../shared/store/media.store.tsx'
import { reduceAudioBufferVolume } from '@/utils/reduce-audiobuffer-volume.ts'
import { updateClip } from './update-clip.ts'
import { useHistoryStore } from '../../../../shared/store/history.store.tsx'

const SOUND_TEMPLATE_DURATION_EPSILON_SEC = 0.1

const isCachedBufferShorterThanRequested = (
  buffer: AudioBuffer,
  requestedDurationSec: number
): boolean =>
  buffer.duration + SOUND_TEMPLATE_DURATION_EPSILON_SEC < requestedDurationSec

export const addDraggingSoundDesignClip = async (
  alias: string,
  name: string,
  duration: number,
  sessionId: string
): Promise<void> => {
  const draggingClip = addDraggingClip(
    TrackType.SoundDesigns,
    alias,
    name,
    duration
  )
  const { setDraggedClip } = useTracksStore.getState()
  const { media, addMedia } = useMediaStore.getState()

  let audioBuffer = media[alias]
  let audioformId: string = crypto.randomUUID()
  const shouldGenerateFullTemplate =
    !audioBuffer || isCachedBufferShorterThanRequested(audioBuffer, duration)

  if (shouldGenerateFullTemplate) {
    // Mark state as dirty when sound design generation starts
    // This prevents user from leaving while sound design is loading
    useHistoryStore.setState({ saveStatus: 'dirty' })

    const {
      id,
      audioBuffer: generatedBuffer,
      url,
    } = await generateSoundDesign(alias, duration, sessionId)
    // we need to use the audioformId as the clip id since this is unique
    audioformId = id
    audioBuffer = reduceAudioBufferVolume(generatedBuffer)
    addMedia(alias, audioBuffer)
    updateClip(draggingClip.id, { src: url })
  }

  if (!audioBuffer) {
    return
  }

  // if the clip has been dropped before loading the buffer
  const currentDraggedClip = useTracksStore.getState().draggedClip
  if (currentDraggedClip?.id !== draggingClip.id) {
    // todo: since this happens async, we already stored a state in the history with the previous id,
    // todo: if this happened, we need to somehow replace the id in the history
    useTracksStore.getState().updateClip(draggingClip.id, { id: audioformId })
  } else {
    // Get the current dragged clip from store to preserve src property that was set via updateClip
    const updatedClip = useTracksStore.getState().draggedClip
    setDraggedClip({
      ...(updatedClip ?? draggingClip),
      id: audioformId,
      duration: audioBuffer.duration,
    })
  }
}
